import * as React from 'react';
import { createPortal } from 'react-dom';

export interface SettingsSuggestionPopoverProps {
	settingsValue: string;
	onApply: () => void;
	placement?: 'below' | 'inline';
}

export const SettingsSuggestionPopover: React.FC<
	SettingsSuggestionPopoverProps
> = ({ settingsValue, onApply, placement = 'below' }) => {
	return (
		<div
			role="dialog"
			aria-label="From your settings:"
			className={
				placement === 'inline'
					? 'relative min-w-[240px] rounded-lg border border-dark-[#EDEDED] bg-white py-2 shadow-shadow2'
					: 'absolute left-0 top-full z-30 mt-1 min-w-[240px] rounded-lg border border-dark-[#EDEDED] bg-white py-2'
			}
		>
			<p className="!text-[#E17100] font-semibold mb-1 text-left px-2 text-base lg:text-xs">
				From your settings:
			</p>
			<button
				type="button"
				onMouseDown={(event) => event.preventDefault()}
				onClick={onApply}
				className="w-full rounded-md px-2 py-1 text-left hover:bg-[#F9F9F9]"
			>
				<p className="!text-left text-base lg:text-xs">{settingsValue}</p>
			</button>
		</div>
	);
};

export interface FloatingSettingsSuggestionProps {
	anchorRef: React.RefObject<HTMLElement>;
	popoverRef?: React.RefObject<HTMLDivElement>;
	settingsValue: string;
	onApply: () => void;
}

export const FloatingSettingsSuggestion: React.FC<
	FloatingSettingsSuggestionProps
> = ({ anchorRef, popoverRef, settingsValue, onApply }) => {
	const [point, setPoint] = React.useState<{
		left: number;
		top: number;
	} | null>(null);

	React.useLayoutEffect(() => {
		const update = () => {
			const rect = anchorRef.current?.getBoundingClientRect();
			if (!rect) return;
			setPoint({ left: rect.left, top: rect.top });
		};
		update();
		window.addEventListener('scroll', update, true);
		window.addEventListener('resize', update);
		return () => {
			window.removeEventListener('scroll', update, true);
			window.removeEventListener('resize', update);
		};
	}, [anchorRef]);

	if (!point || typeof document === 'undefined') return null;

	// createPortal is typed with the React 19 copy nested under @types/react-dom.
	return createPortal(
		<div
			ref={popoverRef}
			style={{
				position: 'fixed',
				left: point.left,
				top: point.top - 4,
				transform: 'translateY(-100%)',
				zIndex: 1000,
			}}
		>
			<SettingsSuggestionPopover
				placement="inline"
				settingsValue={settingsValue}
				onApply={onApply}
			/>
		</div>,
		document.body
	) as unknown as React.ReactPortal;
};

export default SettingsSuggestionPopover;
